import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid3X3, Key } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  digramsProcessed: number;
  uniqueKeywords: number;
  averageKeywordLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyword: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  digramsCount: number;
}

const PlayfairCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('MONARCHY');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'keysquare' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(true);
  const [fillerLetter, setFillerLetter] = useState<string>('X');
  const [jReplacesI, setJReplacesI] = useState<boolean>(true);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [showKeySquare, setShowKeySquare] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    digramsProcessed: 0,
    uniqueKeywords: 0,
    averageKeywordLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [usedKeywords, setUsedKeywords] = useState<Set<string>>(new Set());
  const [keySquare, setKeySquare] = useState<string[][]>([]);

  // Generate Playfair key square
  const generateKeySquare = useCallback((key: string): string[][] => {
    const cleanKey = key.replace(/[^A-Z]/g, '').toUpperCase();
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let keyString = '';
    const used = new Set<string>();
    
    // Add unique letters from keyword
    for (const char of cleanKey) {
      if (!used.has(char) && char !== 'J') {
        keyString += char;
        used.add(char);
      }
    }
    
    // Add remaining letters (excluding J if jReplacesI is true)
    for (const char of alphabet) {
      if (!used.has(char) && !(jReplacesI && char === 'J')) {
        keyString += char;
        used.add(char);
      }
    }
    
    // Create 5x5 grid
    const square: string[][] = [];
    for (let i = 0; i < 5; i++) {
      square[i] = [];
      for (let j = 0; j < 5; j++) {
        square[i][j] = keyString[i * 5 + j];
      }
    }
    
    return square;
  }, [jReplacesI]);

  // Find position of letter in key square
  const findPosition = useCallback((letter: string, square: string[][]): [number, number] => {
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        if (square[i][j] === letter) {
          return [i, j];
        }
      }
    }
    return [-1, -1];
  }, []);

  // Prepare text for Playfair cipher
  const prepareText = useCallback((text: string): string => {
    let prepared = text.replace(/[^A-Z]/g, '').toUpperCase();
    if (jReplacesI) {
      prepared = prepared.replace(/J/g, 'I');
    }
    
    // Insert filler letters between repeated characters in digrams
    let result = '';
    for (let i = 0; i < prepared.length; i += 2) {
      if (i + 1 < prepared.length) {
        if (prepared[i] === prepared[i + 1]) {
          result += prepared[i] + fillerLetter;
          i--; // Process the second character again
        } else {
          result += prepared[i] + prepared[i + 1];
        }
      } else {
        result += prepared[i] + fillerLetter; // Add filler for odd length
      }
    }
    
    return result;
  }, [fillerLetter, jReplacesI]);

  // Playfair cipher implementation
  const playfairCipher = useCallback((text: string, key: string, isEncrypt: boolean): string => {
    if (!text || !key) return '';
    
    const square = generateKeySquare(key);
    setKeySquare(square);
    
    const preparedText = prepareText(text);
    let result = '';
    
    // Process digrams
    for (let i = 0; i < preparedText.length; i += 2) {
      const char1 = preparedText[i];
      const char2 = preparedText[i + 1];
      
      const [row1, col1] = findPosition(char1, square);
      const [row2, col2] = findPosition(char2, square);
      
      if (row1 === -1 || row2 === -1) continue;
      
      let newChar1, newChar2;
      
      if (row1 === row2) {
        // Same row - move horizontally
        if (isEncrypt) {
          newChar1 = square[row1][(col1 + 1) % 5];
          newChar2 = square[row2][(col2 + 1) % 5];
        } else {
          newChar1 = square[row1][(col1 + 4) % 5];
          newChar2 = square[row2][(col2 + 4) % 5];
        }
      } else if (col1 === col2) {
        // Same column - move vertically
        if (isEncrypt) {
          newChar1 = square[(row1 + 1) % 5][col1];
          newChar2 = square[(row2 + 1) % 5][col2];
        } else {
          newChar1 = square[(row1 + 4) % 5][col1];
          newChar2 = square[(row2 + 4) % 5][col2];
        }
      } else {
        // Rectangle - swap columns
        newChar1 = square[row1][col2];
        newChar2 = square[row2][col1];
      }
      
      result += newChar1 + newChar2;
    }
    
    // Format output
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += 2) {
        groups.push(result.slice(i, i + 2));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generateKeySquare, prepareText, findPosition, fillerLetter, groupOutput]);

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    if (!keyword.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = playfairCipher(inputText, keyword, operation === 'encrypt');
    setOutputText(result);
    return result;
  }, [inputText, keyword, operation, playfairCipher]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !keyword.trim() || !result) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const digramsCount = Math.ceil(cleanInput.length / 2);
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyword: keyword,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      digramsCount: digramsCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setUsedKeywords(prev => {
      const newSet = new Set(prev).add(keyword.toUpperCase());
      
      setStats(prevStats => ({
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: prevStats.totalOperations + 1,
        totalCharactersProcessed: prevStats.totalCharactersProcessed + inputText.length,
        averageTextLength: Math.round((prevStats.totalCharactersProcessed + inputText.length) / (prevStats.totalOperations + 1)),
        longestText: Math.max(prevStats.longestText, inputText.length),
        digramsProcessed: prevStats.digramsProcessed + digramsCount,
        uniqueKeywords: newSet.size,
        averageKeywordLength: Math.round(Array.from(newSet).reduce((sum, k) => sum + k.length, 0) / newSet.size)
      }));
      
      return newSet;
    });
  }, [inputText, keyword, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && keyword) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyword, operation]); // Removed processText from dependencies

  // Update key square when keyword changes
  useEffect(() => {
    if (keyword) {
      setKeySquare(generateKeySquare(keyword));
    }
  }, [keyword, generateKeySquare]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyword('MONARCHY');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "HIDE THE GOLD IN THE TREE STUMP",
      "MEET ME AT MIDNIGHT",
      "THE ENEMY KNOWS OUR PLANS",
      "ATTACK AT DAWN ON TUESDAY",
      "SECRET MESSAGE FOR AGENT SMITH",
      "PLAYFAIR CIPHER IS QUITE SECURE",
      "BALLOON ATTACK POSTPONED UNTIL FURTHER NOTICE"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKeyword = () => {
    const words = [
      'MONARCHY', 'CRYPTOGRAPHY', 'SECURITY', 'PLAYFAIR', 
      'BATTLEFIELD', 'TELEGRAPH', 'DIPLOMACY', 'ESPIONAGE',
      'INTELLIGENCE', 'COMMUNICATION', 'CLASSIFIED', 'OPERATION'
    ];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setKeyword(randomWord);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Playfair Cipher - Advanced Digraph Substitution Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Playfair Cipher tool with 5×5 key square, digraph encryption, and historical military cipher features. Perfect for cryptography education, security training, and classical cipher research." />
        <meta name="keywords" content="playfair cipher, digraph cipher, 5x5 key square, classical cryptography, military cipher, wheatstone cipher, substitution cipher, historical encryption, cryptanalysis, security education, cipher square" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Playfair Cipher - Advanced Digraph Substitution Tool" />
        <meta property="og:description" content="Professional Playfair Cipher with 5×5 key square visualization, real-time digraph processing, and military-grade historical encryption features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Playfair Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Playfair Cipher - Historical Military Encryption" />
        <meta name="twitter:description" content="Professional Playfair Cipher with key square visualization and educational features. Free classical cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@playfaircipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Playfair Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Historical & Military Keywords */}
        <meta name="subject" content="Military History, Cryptography, Classical Ciphers, World War History" />
        <meta name="classification" content="Education, History, Military Science, Computer Science" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Playfair Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Playfair Cipher Tool",
            "alternateName": ["Playfair Encoder Decoder", "Digraph Cipher Tool", "Wheatstone Cipher"],
            "description": "Advanced Playfair Cipher tool for digraph substitution encryption with 5×5 key square visualization, historical military cipher features, and educational resources.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Playfair encryption and decryption",
              "5×5 key square visualization",
              "Digraph substitution cipher",
              "Historical military cipher implementation",
              "Key square generation and display",
              "Advanced cipher options and settings",
              "Analytics and usage statistics",
              "Educational cipher information",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Playfair Cipher Tool",
              "description": "Historical cryptography and military cipher education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Military History and Cryptography Education",
              "targetName": "Classical Cryptography and Historical Military Communications"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research", "historical study"],
            "typicalAgeRange": "16-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Military Cryptography"
              },
              {
                "@type": "Thing",
                "name": "World War Communications"
              },
              {
                "@type": "Thing",
                "name": "Classical Cipher Systems"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1234",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Historical Military Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Playfair Cipher Historical Implementation",
            "description": "Digital recreation of the Playfair cipher system used extensively in military communications during World War I and other historical conflicts.",
            "dateCreated": "1854",
            "creator": {
              "@type": "Person",
              "name": "Charles Wheatstone"
            },
            "about": {
              "@type": "HistoricalEvent",
              "name": "Military Cryptography Development"
            },
            "culturalContext": "Victorian Era Military Communications",
            "historicalSignificance": "First practical digraph substitution cipher used in military communications"
          })}
        </script>
        
        {/* Educational Military History Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Playfair Cipher Military History Tutorial",
            "description": "Learn about military cryptography through hands-on interaction with the historic Playfair cipher system used in WWI and WWII communications.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Digraph substitution ciphers",
              "Military cryptography history",
              "5×5 key square construction",
              "World War communication methods",
              "Classical cipher analysis"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "research", "historical analysis"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "historian", "researcher"],
              "audienceType": ["military history student", "cryptography enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Military History"
              },
              {
                "@type": "Thing",
                "name": "World War Communications"
              },
              {
                "@type": "Thing",
                "name": "Cryptanalysis"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Grid3X3 className="text-red-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Playfair Cipher</h1>
                <p className="text-sm opacity-60">Digraph Substitution Cipher (1854)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Key Square */}
              <button
                onClick={() => setActiveModal(activeModal === 'keysquare' ? null : 'keysquare')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Key Square"
              >
                <Grid3X3 size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Keyword</label>
                    <button
                      onClick={generateRandomKeyword}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                      } text-white transition-colors`}
                      title="Generate Random Keyword"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter keyword for cipher..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-red-600 border-red-500 hover:bg-red-700 text-white' : 'bg-red-500 border-red-400 hover:bg-red-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Key Square Preview */}
              {showKeySquare && keySquare.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Current Key Square:</h4>
                  <div className="grid grid-cols-5 gap-1 w-fit mx-auto">
                    {keySquare.flat().map((letter, index) => (
                      <div
                        key={index}
                        className={`w-8 h-8 flex items-center justify-center text-sm font-mono font-bold border ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Playfair cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'playfair-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Key Square Modal */}
        {activeModal === 'keysquare' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid3X3 className="text-red-500" />
                  <span>Key Square</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="text-center">
                <div className="mb-4">
                  <div className="text-sm opacity-60 mb-2">Keyword: <strong>{keyword}</strong></div>
                </div>
                
                {keySquare.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 w-fit mx-auto mb-4">
                    {keySquare.flat().map((letter, index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 flex items-center justify-center text-lg font-mono font-bold border-2 ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                        }`}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="text-xs opacity-60 space-y-1">
                  <div>Row and column indices (0-4)</div>
                  <div>J is typically replaced with I</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-red-500" />
                  <span>About Playfair Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Playfair Cipher?</h3>
                  <p>The Playfair cipher is a digraph substitution cipher, invented by Charles Wheatstone in 1854 but popularized by Lord Playfair. It encrypts pairs of letters (digrams) using a 5×5 key square, making it much more secure than simple substitution ciphers.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create a 5×5 key square using a keyword</li>
                    <li>Remove duplicate letters from the keyword</li>
                    <li>Fill remaining spaces with unused alphabet letters</li>
                    <li>Typically combine I and J into one position</li>
                    <li>Encrypt text in pairs (digrams) using square rules</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Rules</h3>
                  <ul className="space-y-1">
                    <li><strong>Same Row:</strong> Move each letter one position to the right (wrap around)</li>
                    <li><strong>Same Column:</strong> Move each letter one position down (wrap around)</li>
                    <li><strong>Rectangle:</strong> Swap columns - each letter takes the other's column position</li>
                    <li><strong>Repeated Letters:</strong> Insert filler letter (usually X) between identical pairs</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example with keyword "MONARCHY"</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="grid grid-cols-5 gap-1 w-fit mb-3 font-mono text-xs">
                      {['M','O','N','A','R','C','H','Y','B','D','E','F','G','I','K','L','P','Q','S','T','U','V','W','X','Z'].map((letter, index) => (
                        <div key={index} className="w-6 h-6 flex items-center justify-center border border-gray-400 bg-gray-200 text-gray-900">
                          {letter}
                        </div>
                      ))}
                    </div>
                    <div className="text-xs">
                      <div><strong>Plain:</strong> HI DE TH EG OL DX</div>
                      <div><strong>Cipher:</strong> BM OD ZH XG MQ LV</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Military History</h3>
                  <ul className="space-y-1">
                    <li><strong>World War I:</strong> Used extensively by British forces</li>
                    <li><strong>Boer War:</strong> First major military deployment</li>
                    <li><strong>World War II:</strong> Used by various Allied forces</li>
                    <li><strong>Tactical Communications:</strong> Field-level secure messaging</li>
                    <li><strong>Telegraph Security:</strong> Protected long-distance communications</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Much stronger than monoalphabetic ciphers</li>
                    <li>Digram frequency analysis is more difficult</li>
                    <li>Can be performed manually without equipment</li>
                    <li>Relatively fast encryption/decryption process</li>
                    <li>Good security for field communications</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Vulnerable to frequency analysis of digrams</li>
                    <li>Known plaintext attacks are possible</li>
                    <li>Limited by 5×5 square structure</li>
                    <li>J/I substitution reduces alphabet size</li>
                    <li>Pattern analysis can reveal structure</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and training</li>
                    <li>Historical cipher demonstrations</li>
                    <li>Puzzle and game creation</li>
                    <li>Understanding digram analysis</li>
                    <li>Classical cryptanalysis practice</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Playfair cipher provided good security for its time but is easily broken by modern cryptanalytic methods. It's excellent for education but not suitable for actual security needs.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">J replaces I</label>
                  <input
                    type="checkbox"
                    checked={jReplacesI}
                    onChange={(e) => setJReplacesI(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output (Digrams)</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Key Square</label>
                  <input
                    type="checkbox"
                    checked={showKeySquare}
                    onChange={(e) => setShowKeySquare(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Filler Letter</label>
                  <input
                    type="text"
                    value={fillerLetter}
                    onChange={(e) => setFillerLetter(e.target.value.replace(/[^A-Z]/g, '').slice(0, 1))}
                    placeholder="X"
                    maxLength={1}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Letter inserted between repeated characters in digrams
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keywords</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['MONARCHY', 'PLAYFAIR', 'WHEATSTONE', 'TELEGRAPH', 'MILITARY', 'WARFARE'].map(key => (
                      <button
                        key={key}
                        onClick={() => setKeyword(key)}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Military Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setPreserveCase(false);
                        setRemoveSpaces(true);
                        setJReplacesI(true);
                        setGroupOutput(true);
                        setFillerLetter('X');
                        setKeyword('MONARCHY');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-red-600 border-red-500 hover:bg-red-700 text-white' : 'bg-red-500 border-red-400 hover:bg-red-600 text-white'
                      } transition-colors`}
                    >
                      WWI British Standard
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(true);
                        setRemoveSpaces(false);
                        setJReplacesI(true);
                        setGroupOutput(false);
                        setFillerLetter('X');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.digramsProcessed}</div>
                  <div className="text-sm">Digrams Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeywords}</div>
                  <div className="text-sm">Unique Keywords</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeywordLength}</div>
                  <div className="text-sm">Avg Keyword Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Avg Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.digramsCount} digrams
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-red-500 text-white">
                            {item.keyword}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Grid3X3 size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Digrams per Operation:</div>
                      <div>{stats.totalOperations > 0 ? Math.round(stats.digramsProcessed / stats.totalOperations) : 0}</div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.digramsProcessed * 2 / stats.totalCharactersProcessed) * 100) : 0}% Processed</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PlayfairCipherApp;